import axios, { AxiosInstance } from 'axios';
import type { ProductListResponse, ProductDetail, ProductComment, DashboardStats, ReviewStatus } from '../types/product';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private http: AxiosInstance;
  private tokenGetter: (() => string | null) | null = null;
  private tokenRefresher: (() => Promise<string | null>) | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.http = axios.create({
      baseURL: API_BASE,
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.http.interceptors.request.use((config) => {
      const token = this.tokenGetter?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry && this.tokenRefresher) {
          originalRequest._retry = true;
          try {
            if (!this.refreshPromise) {
              this.refreshPromise = this.tokenRefresher().finally(() => { this.refreshPromise = null; });
            }
            const newToken = await this.refreshPromise;
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.http(originalRequest);
            }
          } catch {
            // refresh failed, fall through to reject
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setTokenGetter(getter: () => string | null) {
    this.tokenGetter = getter;
  }

  setTokenRefresher(refresher: () => Promise<string | null>) {
    this.tokenRefresher = refresher;
  }

  // Products
  async listProducts(params: {
    category?: string;
    brand?: string;
    status?: string;
    search?: string;
    min_score?: number;
    max_score?: number;
    page_size?: number;
    last_key?: string;
  } = {}): Promise<ProductListResponse> {
    const { data } = await this.http.get('/products', { params });
    return data;
  }

  async getProduct(cprcode: string): Promise<ProductDetail> {
    const { data } = await this.http.get(`/products/${cprcode}`);
    return data;
  }

  async updateProduct(cprcode: string, updates: Partial<ProductDetail>): Promise<ProductDetail> {
    const { data } = await this.http.put(`/products/${cprcode}`, updates);
    return data;
  }

  async bulkEdit(cprcodes: string[], updates: Record<string, unknown>): Promise<{ updated: number }> {
    const { data } = await this.http.post('/products/bulk-edit', { cprcodes, updates });
    return data;
  }

  async exportProducts(filters: Record<string, unknown>, fields?: string[]): Promise<{ url: string }> {
    const { data } = await this.http.post('/products/export', { filters, fields });
    return data;
  }

  // Comments
  async getComments(cprcode: string): Promise<{ comments: ProductComment[]; count: number }> {
    const { data } = await this.http.get(`/products/${cprcode}/comments`);
    return data;
  }

  async addComment(cprcode: string, text: string): Promise<{ comment_id: string }> {
    const { data } = await this.http.post(`/products/${cprcode}/comments`, { text });
    return data;
  }

  async reviewProduct(cprcode: string, status: ReviewStatus, note?: string): Promise<ProductDetail> {
    const updates: Record<string, unknown> = { review_status: status };
    if (note) updates.review_note = note;
    const { data } = await this.http.put(`/products/${cprcode}`, updates);
    return data;
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const { data } = await this.http.get('/dashboard/stats');
    return data;
  }

  // Sync
  async syncSellingStatus(): Promise<{ total_synced: number; newly_selling: number; newly_not_selling: number }> {
    const { data } = await this.http.post('/sync/selling-status');
    return data;
  }
}

export const api = new ApiClient();
export default api;
